/* Local BH scoped-deployment candidate. Not installed or published.
 * Reuse requires authenticated SDK load plus two real target getter results
 * on the same current function/layer, even when configuration is visible. Readable identity is not proof of configuration or receipt.
 * This candidate does not initialize a missing target.
 * Existing opt-in exact-Draft owned fallback remains isolated and unchanged.
 * Never emits manual page_view/commerce, identity or advertising consent commands.
 * Exact-Draft owned fallback alone mirrors official Shopify analytics consent. */
(function (scope, factory) {
  'use strict';
  var deploymentApi = typeof module === 'object' && module.exports ? require('../bh-deployment-scope.js') : scope && scope.BhDeploymentScope;
  var api = factory(deploymentApi);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (scope && scope.document) {
    scope.BhGA4Adapter = api;
    api.bootstrap(scope);
  }
})(typeof window !== 'undefined' ? window : null, function (deploymentApi) {
  'use strict';

  var MEASUREMENT_ID = 'G-8ZW5BJNP24';
  // The merchant's merged Google tag is the native-verified SDK loader ID.
  // G8 remains the exclusive GA4 measurement/config/event destination.
  var BOOTSTRAP_TAG_ID = 'GT-PHCDRJ3R';
  var ADAPTER_VERSION = '1.6.9-deployment-scope-candidate';
  var AREA_ENTRIES = {
    home_banner: ['image', 'cta'], announcement: ['cta'], menu: ['menu_item'],
    collection_promo: ['image', 'cta'], load_more: ['button'], description: ['accordion_header']
  };
  var SCENE_KEYS = {
    home_banner: ['banner_index'], announcement: ['position'],
    menu: ['menu_type', 'menu_item_id', 'position'],
    collection_promo: ['collection_handle', 'position'],
    load_more: ['collection_handle'], description: ['product_handle']
  };
  var PAGE_TYPES = ['home', 'collection', 'product', 'page'];

  function validKey(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 100 &&
      /^[a-zA-Z0-9_:.\-]+$/.test(value) && !/^\d{7,}$/.test(value);
  }

  function boundedInteger(value, fallback, minimum, maximum) {
    var number = Number(value);
    if (!Number.isFinite(number)) number = fallback;
    return Math.min(maximum, Math.max(minimum, Math.floor(number)));
  }

  function createAdapter(options) {
    options = options || {};
    var win = options.window || (typeof window !== 'undefined' ? window : null);
    if (!win || !win.document) throw new Error('A storefront window is required.');
    var doc = win.document;
    var measurementId = options.measurementId || MEASUREMENT_ID;
    if (measurementId !== MEASUREMENT_ID) throw new Error('Unexpected GA4 measurement destination.');
    if (options.bootstrapTagId != null && options.bootstrapTagId !== BOOTSTRAP_TAG_ID) throw new Error('Unexpected Google SDK loader tag.');
    var route = options.route || 'reuse';
    if (route !== 'reuse') throw new Error('Unknown GA4 route.');
    var explicitDeployment = Object.prototype.hasOwnProperty.call(options, 'deployment');
    var deploymentDescriptor = Object.getOwnPropertyDescriptor(options, 'deployment');
    var deployment = deploymentDescriptor && Object.prototype.hasOwnProperty.call(deploymentDescriptor, 'value') ? deploymentDescriptor.value : undefined;
    function resolvedDeployment() {
      try {
        var theme = win.Shopify && win.Shopify.theme;
        return deploymentApi && deploymentApi.resolve(deployment, {
          themeId: theme && theme.id, role: theme && theme.role,
          hostname: new win.URL(win.location.href).hostname
        });
      } catch (_) { return null; }
    }
    var initialDeployment = explicitDeployment ? resolvedDeployment() : null;
    function deploymentAllowed() {
      if (!explicitDeployment) return true;
      var current = resolvedDeployment();
      return !!initialDeployment && !!current && current.storageKey === initialDeployment.storageKey;
    }
    var fallbackDelay = boundedInteger(options.fallbackDetectionMs == null ? 1000 : options.fallbackDetectionMs, 1000, 250, 2000);
    var fallbackTimer = null;
    var fallbackChecked = false;
    var fallbackReason = 'not_requested';
    var fallbackConfigSubmitted = false;
    var fallbackState = null;
    var pageHidden = false;
    var bfcacheRestores = 0;
    var maxQueue = boundedInteger(options.queueMax == null ? 20 : options.queueMax, 20, 0, 20);
    var queueTtl = boundedInteger(options.queueTtlMs == null ? 3000 : options.queueTtlMs, 3000, 0, 3000);
    // In-page pagination clicks may wait for a slow SDK; navigation keeps its existing short window.
    var loadMoreQueueTtl = boundedInteger(options.loadMoreQueueTtlMs == null ? 30000 : options.loadMoreQueueTtlMs, 30000, 0, 30000);
    var probeTimeout = boundedInteger(options.probeTimeoutMs || 1000, 1000, 100, 5000);
    var maxProbes = boundedInteger(options.maxProbes || 10, 10, 1, 20);
    var now = options.now || function () { return Date.now(); };
    var started = false;
    var destroyed = false;
    var privacyReady = false;
    var allowed = false;
    var transportReady = false;
    var transportProbeTimer = null;
    var retryTimer = null;
    var queueTimer = null;
    var readinessBasis = 'none';
    var scriptObserver = null;
    var observerBatchTimer = null;
    var identityProbeCount = 0;
    var identityProbeState = 'not_requested';
    var releaseAttempts = 0;
    var probeVersion = 0;
    var probeCount = 0;
    var preflightProbeDone = false;
    var existingGoogleTagReleaseRequested = false;
    var clientIdPresent = false;
    var sessionIdPresent = false;
    var unobservedConfigIdentityReadable = false;
    var identityGtag = null;
    var identityLayer = null;
    // Correlate both configured and getter-only reuse with one current owner.
    // These references are never copied to diagnostics or used to create a layer.
    var readyOwnerSeen = false;
    var readyOwnerGtag = null;
    var readyOwnerLayer = null;
    var ownerRevalidationRequired = false;
    var pendingOwnerGtag = null;
    var pendingOwnerLayer = null;
    var queue = [];
    var handoff = null;
    var handoffChecked = false;
    var pendingHandoff = null;
    var handoffTimer = null;
    var handoffTimingEvidence = null;
    var timingLastEventAtMs = null;
    var recentCommands = [];
    var diagnosticNode = null;
    var counters = {
      received: 0, sentCommands: 0, denied: 0, invalid: 0, queued: 0,
      expired: 0, overflow: 0, consentCleared: 0, errors: 0, omittedUrls: 0,
      processingCallbacks: 0, handoffStored: 0, handoffClaimed: 0,
      handoffSubmitted: 0, handoffExpired: 0, handoffDropped: 0
    };
    var lastReason = 'not_started';

    function commandFunction() {
      if (typeof win.gtag === 'function') {
        return { type: 'main_gtag', call: function () { return win.gtag.apply(win, arguments); } };
      }
      if (Array.isArray(win.dataLayer)) {
        return {
          type: 'private_existing_data_layer',
          call: function () {
            // Use only an existing layer. Never create a second layer or replace main gtag.
            if (!Array.isArray(win.dataLayer)) throw new Error('Existing data layer is unavailable.');
            win.dataLayer.push(arguments);
          }
        };
      }
      return { type: 'unavailable', call: null };
    }

    function matchingDraft() {
      if (explicitDeployment && (!deploymentAllowed() || initialDeployment.mode !== 'draft')) return false;
      var settings = options.diagnostics || {};
      var theme = win.Shopify && win.Shopify.theme;
      return settings.draftThemeId != null && String(settings.draftThemeId).length > 0 && !!theme &&
        String(settings.draftThemeId) === String(theme.id) && !!theme.role && theme.role !== 'main';
    }

    function draftDiagnosticsEnabled() {
      return (options.diagnostics || {}).enabled === true && matchingDraft();
    }

    function handoffTimingEnabled() {
      try {
        return draftDiagnosticsEnabled() && String((options.diagnostics || {}).draftThemeId) === '196364992838';
      } catch (_) { return false; }
    }

    function timingForWrite() {
      if (!handoffTimingEnabled()) {
        handoffTimingEvidence = null; timingLastEventAtMs = null; return null;
      }
      if (!handoffTimingEvidence) handoffTimingEvidence = {
        schema: 1,
        recordCreatedAtMs: null, recordDeadlineAtMs: null, recordStoredAtMs: null,
        recordClaimedAtMs: null, recordClearedAtMs: null,
        analyticsAllowedAtMs: null, analyticsDeniedAtMs: null, analyticsUnknownAtMs: null,
        reallowedAtMs: null, sdkLoadedNotifiedAtMs: null, targetTransportReadyAtMs: null,
        analyticsConsentState: 'unknown', recordClearReason: 'none', clockRollbackDetected: false
      };
      return handoffTimingEvidence;
    }

    function timingEpoch(value) {
      return Number.isSafeInteger(value) && value >= 0 && value <= 8640000000000000 ? value : null;
    }

    function timingEventEpoch(state) {
      var timestamp;
      try { timestamp = timingEpoch(now()); } catch (_) { timestamp = null; }
      if (timestamp === null || (timingLastEventAtMs !== null && timestamp < timingLastEventAtMs)) {
        state.clockRollbackDetected = true;
      }
      if (timestamp !== null) timingLastEventAtMs = timingLastEventAtMs === null ? timestamp : Math.max(timingLastEventAtMs, timestamp);
      return timestamp;
    }

    function noteTimingConsent(value) {
      var state = timingForWrite();
      if (!state) return;
      var consentState = value === true ? 'allowed' : (value === false ? 'denied' : 'unknown');
      var field = consentState === 'allowed' ? 'analyticsAllowedAtMs' :
        (consentState === 'denied' ? 'analyticsDeniedAtMs' : 'analyticsUnknownAtMs');
      if (state.analyticsConsentState === consentState && state[field] !== null) return;
      var timestamp = timingEventEpoch(state);
      if (consentState === 'allowed' && state.analyticsConsentState !== 'allowed' && state.analyticsDeniedAtMs !== null) state.reallowedAtMs = timestamp;
      if (consentState === 'denied') state.reallowedAtMs = null;
      state.analyticsConsentState = consentState; state[field] = timestamp;
    }

    function noteTimingRecord(createdAt, stage) {
      var state = timingForWrite();
      if (!state) return;
      var original = timingEpoch(createdAt);
      if (original === null) return;
      state.recordCreatedAtMs = original;
      state.recordDeadlineAtMs = timingEpoch(original + 45000);
      state.recordStoredAtMs = null; state.recordClaimedAtMs = null;
      state.recordClearedAtMs = null; state.recordClearReason = 'none';
      state[stage === 'stored' ? 'recordStoredAtMs' : 'recordClaimedAtMs'] = timingEventEpoch(state);
    }

    function noteTimingClear(reason) {
      var state = timingForWrite();
      if (!state || state.recordCreatedAtMs === null) return;
      if (state.recordClearedAtMs === null) state.recordClearedAtMs = timingEventEpoch(state);
      if (state.recordClearReason === 'none' || state.recordClearReason === 'submission_attempt') state.recordClearReason = reason;
    }

    function noteTimingReadiness(field) {
      var state = timingForWrite();
      if (!state || (field === 'sdkLoadedNotifiedAtMs' && state[field] !== null)) return;
      state[field] = timingEventEpoch(state);
    }

    function timingSnapshot() {
      if (!handoffTimingEnabled() || !handoffTimingEvidence) return null;
      var state = Object.assign({}, handoffTimingEvidence);
      var observed;
      try { observed = timingEpoch(now()); } catch (_) { observed = null; }
      state.observedAtMs = observed;
      state.clockRollbackDetected = state.clockRollbackDetected || observed === null ||
        (timingLastEventAtMs !== null && observed < timingLastEventAtMs);
      state.recordAgeMs = state.recordCreatedAtMs !== null && observed !== null && observed >= state.recordCreatedAtMs ? observed - state.recordCreatedAtMs : null;
      state.reallowRecordAgeMs = state.recordCreatedAtMs !== null && state.reallowedAtMs !== null &&
        state.reallowedAtMs >= state.recordCreatedAtMs ? state.reallowedAtMs - state.recordCreatedAtMs : null;
      state.withinOriginalTtl = state.recordCreatedAtMs === null ? null :
        !state.clockRollbackDetected && state.recordAgeMs !== null && state.recordAgeMs < 45000;
      state.reallowedWithinOriginalTtl = state.recordCreatedAtMs === null || state.reallowedAtMs === null ? null :
        !state.clockRollbackDetected && state.reallowRecordAgeMs !== null && state.reallowRecordAgeMs < 45000;
      return state;
    }

    function publishDiagnostics() {
      if (!draftDiagnosticsEnabled()) return;
      try {
        diagnosticNode = diagnosticNode || doc.getElementById('bh-ga4-diagnostics');
        if (!diagnosticNode) {
          diagnosticNode = doc.createElement('pre');
          diagnosticNode.id = 'bh-ga4-diagnostics';
          diagnosticNode.setAttribute('aria-label', 'BH Draft GA4 diagnostics');
          diagnosticNode.style.cssText = 'position:fixed;right:8px;bottom:90px;z-index:2147483000;box-sizing:border-box;width:min(360px,calc(100vw - 16px));white-space:pre-wrap;font-size:10px;max-height:140px;overflow:auto;margin:0;padding:8px;background:#fff;color:#222;border:1px solid #ddd;pointer-events:none;';
          (doc.body || doc.documentElement).appendChild(diagnosticNode);
        }
        diagnosticNode.textContent = JSON.stringify(diagnostics(), null, 2);
      } catch (_) { /* Diagnostics must never affect storefront behavior. */ }
    }

    function clearTimer(name) {
      if (name === 'probe' && transportProbeTimer !== null) {
        win.clearTimeout(transportProbeTimer); transportProbeTimer = null;
      }
      if (name === 'retry' && retryTimer !== null) {
        win.clearTimeout(retryTimer); retryTimer = null;
      }
      if (name === 'observer' && observerBatchTimer !== null) {
        win.clearTimeout(observerBatchTimer); observerBatchTimer = null;
      }
      if (name === 'queue' && queueTimer !== null) {
        win.clearTimeout(queueTimer); queueTimer = null;
      }
    }

    function stopFallbackTimer() {
      if (fallbackTimer !== null) { win.clearTimeout(fallbackTimer); fallbackTimer = null; }
    }

    function clearQueue(reason) {
      if (reason === 'consent') counters.consentCleared += queue.length;
      queue = [];
      clearTimer('queue');
    }

    function readConsentState() {
      if (!privacyReady || destroyed) return null;
      try {
        var privacy = win.Shopify && win.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
        var value = privacy.analyticsProcessingAllowed();
        return typeof value === 'boolean' ? value : null;
      } catch (_) {
        counters.errors += 1;
        lastReason = 'privacy_read_failed';
        return null;
      }
    }

    function readConsent() { return deploymentAllowed() && readConsentState() === true; }

    function pauseForConsent(clearReason) {
      // Close only BH's early-release exemption; the ordinary loader and App
      // consent/configuration remain their original owners' responsibility.
      win.__bhGoogleTagMayLoad = false;
      allowed = false; transportReady = false; readinessBasis = 'none';
      clientIdPresent = false; sessionIdPresent = false;
      unobservedConfigIdentityReadable = false; identityGtag = null; identityLayer = null;
      identityProbeCount = 0; identityProbeState = 'not_allowed';
      clearQueue('consent'); clearTimer('probe'); clearTimer('retry'); stopFallbackTimer();
      clearPendingHandoff(clearReason || 'consent_unresolved');
      if (handoff) handoff.clear();
      noteTimingClear(clearReason || 'consent_unresolved');
      fallbackChecked = false;
      probeVersion += 1;
      stopScriptObserver();
    }

    function updateConsent() {
      var previouslyAllowed = allowed;
      var officialConsent = readConsentState();
      noteTimingConsent(officialConsent);
      allowed = officialConsent === true;
      syncOwnedAnalyticsConsent();
      if (!allowed) {
        // Initial unresolved consent is not a denial: keep only the prior
        // permitted source record until resolution/TTL. Never send in unknown.
        // A denial, or unknown after permission/claim, terminates the handoff.
        if (officialConsent === false || previouslyAllowed || handoffChecked) pauseForConsent(officialConsent === false ? 'consent_denied' : 'consent_unknown');
        lastReason = officialConsent === false ? 'analytics_not_allowed' : 'privacy_not_ready';
        publishDiagnostics(); return;
      }
      claimNavigationHandoff();
      startScriptObserver();
      requestExistingGoogleTagRelease();
      considerFallback();
      if (refreshReadiness()) {
        flushQueue();
        if (!preflightProbeDone && options.preflightProbe !== false) {
          preflightProbeDone = true; probeIdentity();
        }
      } else if (queue.length || pendingHandoff) prepareTransport();
      publishDiagnostics();
    }

    function requestExistingGoogleTagRelease() {
      if (destroyed || options.enabled !== true || !readConsent() ||
          typeof win.__bhReleaseExistingGoogleTag !== 'function') return;
      // Re-run the existing helper after a late script declaration. Its URL/DOM
      // deduplication owns hydration; only the separate opt-in owned fallback may
      // declare its target SDK/config after the strict absence checks below.
      existingGoogleTagReleaseRequested = true;
      releaseAttempts += 1;
      try { win.__bhReleaseExistingGoogleTag(); }
      catch (_) { counters.errors += 1; lastReason = 'existing_google_tag_release_failed'; }
    }

    function googleConsentEvidence() {
      var result = { complete: false, analyticsGranted: false, defaultSeen: false, regionalDeclarationSeen: false, inspectionFailed: false };
      var values = { analytics_storage: 'unknown', ad_storage: 'unknown', ad_user_data: 'unknown', ad_personalization: 'unknown' };
      var keys = Object.keys(values);
      if (!Array.isArray(win.dataLayer) || win.dataLayer.length > 2000) return result;
      try {
        for (var index = 0; index < win.dataLayer.length; index += 1) {
          var descriptor = Object.getOwnPropertyDescriptor(win.dataLayer, String(index));
          if (!descriptor) continue;
          if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) { result.inspectionFailed = true; break; }
          var entry = descriptor.value;
          if (!entry || (typeof entry !== 'object' && typeof entry !== 'function')) continue;
          var command = Object.getOwnPropertyDescriptor(entry, '0');
          if (!command || command.value !== 'consent') continue;
          var action = Object.getOwnPropertyDescriptor(entry, '1');
          var parameter = Object.getOwnPropertyDescriptor(entry, '2');
          if (!action || !parameter || !Object.prototype.hasOwnProperty.call(parameter, 'value') ||
              !parameter.value || typeof parameter.value !== 'object') { result.inspectionFailed = true; break; }
          if (action.value !== 'default' && action.value !== 'update') continue;
          var settings = parameter.value;
          // Regional defaults cannot be resolved by a theme script. Defer to the
          // platform; the narrow candidate requires one global default declaration.
          var region = Object.getOwnPropertyDescriptor(settings, 'region');
          if (region) {
            if (!Object.prototype.hasOwnProperty.call(region, 'value')) { result.inspectionFailed = true; break; }
            result.regionalDeclarationSeen = true; break;
          }
          var observed = {};
          for (var keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
            var field = Object.getOwnPropertyDescriptor(settings, keys[keyIndex]);
            if (!field) continue;
            if (!Object.prototype.hasOwnProperty.call(field, 'value')) { result.inspectionFailed = true; break; }
            observed[keys[keyIndex]] = field.value === 'granted' || field.value === 'denied' ? field.value : 'unknown';
          }
          if (result.inspectionFailed) break;
          if (action.value === 'default') {
            result.defaultSeen = keys.every(function (key) { return observed[key] === 'granted' || observed[key] === 'denied'; });
            keys.forEach(function (key) { values[key] = observed[key] || 'unknown'; });
          } else keys.forEach(function (key) { if (Object.prototype.hasOwnProperty.call(observed, key)) values[key] = observed[key]; });
        }
      } catch (_) { result.inspectionFailed = true; }
      result.complete = !result.inspectionFailed && !result.regionalDeclarationSeen && result.defaultSeen && keys.every(function (key) { return values[key] === 'granted' || values[key] === 'denied'; });
      result.analyticsGranted = result.complete && values.analytics_storage === 'granted';
      return result;
    }

    function hasEnabledAreaForPage() {
      var areas = options.areas;
      if (!areas || typeof areas !== 'object') return false;
      var path;
      try { path = new win.URL(win.location.href).pathname; } catch (_) { return false; }
      var scopes = options.scopes || {};
      return Object.keys(AREA_ENTRIES).some(function (area) {
        if (areas[area] !== true) return false;
        var areaScope = scopes[area] || {};
        if (Array.isArray(areaScope.pageTypes) && areaScope.pageTypes.indexOf(options.pageType) < 0) return false;
        if (Array.isArray(areaScope.paths) && areaScope.paths.indexOf(path) < 0) return false;
        return true;
      });
    }

    function fallbackScopeAllowed() {
      if (explicitDeployment) {
        return deploymentAllowed() && route === 'reuse' && options.guardedFallback === true && options.enabled === true &&
          (options.pageType === 'home' || options.pageType === 'collection') && hasEnabledAreaForPage() &&
          options.ownedAnalyticsConsentMirror === true;
      }
      return route === 'reuse' && options.guardedFallback === true && options.enabled === true && matchingDraft() &&
          String((win.Shopify && win.Shopify.theme || {}).id) === '196364992838' &&
          (options.pageType === 'home' || options.pageType === 'collection') && hasEnabledAreaForPage() &&
          options.draftOnlyOwnedAnalyticsConsentMirror === true;
    }

    function fallbackAllowed() {
      if (!fallbackScopeAllowed() || destroyed || pageHidden || !readConsent()) return false;
      // A newly loaded SDK can generate automatic measurements outside our single
      // event override. Do not start it on an obviously private page context.
      // This is a conservative heuristic, not complete identification of all PII.
      if (!safeOwnedPageContext()) { fallbackReason = 'owned_bootstrap_private_page_context'; return false; }
      var evidence = googleConsentEvidence();
      return evidence.complete && evidence.analyticsGranted;
    }

    function safeOwnedPageContext() {
      var href = String(win.location.href || '');
      if (hasObviousPii(piiInspectionUrl(href)) || hasObviousPii(doc.title) || hasPrivateReferrer()) return false;
      try { return !isPrivatePath(new win.URL(href).pathname); }
      catch (_) { return false; }
    }

    function syncOwnedAnalyticsConsent() {
      var state = ownedFallbackState();
      if (!state || !state.mirrorActive || !fallbackScopeAllowed()) return false;
      if (win.gtag !== state.originalGtag || win.dataLayer !== state.originalLayer) {
        state.mirrorFailed = true; fallbackReason = 'owned_consent_transport_changed'; return false;
      }
      var environment = mainEnvironment();
      if (!state.configSubmitted && (environment.targetConfigSeen || environment.otherGoogleSdkDeclared)) {
        state.mirrorActive = false; state.appPreferred = true;
        fallbackReason = 'app_arrived_before_owned_config'; return false;
      }
      var evidence = googleConsentEvidence();
      var privacy = win.Shopify && win.Shopify.customerPrivacy;
      var analytics = false;
      try { analytics = !!privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed() === true; }
      catch (_) { state.mirrorFailed = true; }
      // A missing/regional declaration cannot authorize a grant. Unknown official
      // API state is denied; no setTrackingConsent or permission mutation occurs.
      if (!evidence.complete) { analytics = false; state.mirrorFailed = true; }
      // Do not broaden a separately declared Google denial. A subsequent official
      // allow may replace only our own preceding analytics-only denied update.
      if (analytics && !evidence.analyticsGranted && !latestAnalyticsDeclarationIsOwned(state)) {
        fallbackReason = 'google_shopify_analytics_mismatch'; return false;
      }
      var value = analytics ? 'granted' : 'denied';
      if (state.mirrorFailed && value === 'granted') return false;
      if (state.mirrorLast === value) return true;
      // At-most-once for the state transition, even if the wrapper throws after push.
      state.mirrorLast = value;
      state.mirrorParams = { analytics_storage: value };
      try {
        state.originalGtag.apply(win, ['consent', 'update', state.mirrorParams]);
        state.mirrorCommands += 1;
        return true;
      } catch (_) {
        state.mirrorFailed = true; counters.errors += 1;
        fallbackReason = 'owned_analytics_consent_update_failed'; return false;
      }
    }

    function latestAnalyticsDeclarationIsOwned(state) {
      if (!Array.isArray(win.dataLayer) || win.dataLayer.length > 2000 || !state.mirrorParams) return false;
      try {
        for (var index = win.dataLayer.length - 1; index >= 0; index -= 1) {
          var item = Object.getOwnPropertyDescriptor(win.dataLayer, String(index));
          if (!item || !Object.prototype.hasOwnProperty.call(item, 'value')) continue;
          var entry = item.value;
          if (!entry || typeof entry !== 'object') continue;
          var command = Object.getOwnPropertyDescriptor(entry, '0');
          var params = Object.getOwnPropertyDescriptor(entry, '2');
          if (!command || command.value !== 'consent' || !params || !Object.prototype.hasOwnProperty.call(params, 'value') || !params.value) continue;
          if (Object.getOwnPropertyDescriptor(params.value, 'analytics_storage')) return params.value === state.mirrorParams;
        }
      } catch (_) { /* No permission can be inferred from an unreadable declaration. */ }
      return false;
    }

    function ownedGoogleAnalyticsAllowed() {
      var owned = ownedFallbackState();
      if (!owned || !owned.configSubmitted) return true;
      if (owned.status !== 'loaded' || owned.mirrorFailed || !owned.mirrorActive) return false;
      var evidence = googleConsentEvidence();
      return evidence.complete && evidence.analyticsGranted;
    }

    function ownedFallbackState() {
      var descriptor = Object.getOwnPropertyDescriptor(win, '__bhGa4OwnedFallback');
      if (!descriptor) return null;
      if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) return false;
      var state = descriptor.value;
      if (!state || typeof state !== 'object') return false;
      var owner = Object.getOwnPropertyDescriptor(state, 'owner');
      var target = Object.getOwnPropertyDescriptor(state, 'measurementId');
      return owner && owner.value === 'bh-ga4-v15' && target && target.value === measurementId ? state : false;
    }

    function finishOwnedFallback() {
      if (!fallbackAllowed()) return;
      var state = ownedFallbackState();
      if (!state || state.status !== 'loaded') return;
      fallbackState = state;
      if (state.configSubmitted) {
        fallbackConfigSubmitted = true;
        return;
      }
      var environment = mainEnvironment();
      if (environment.inspectionFailed) { fallbackReason = 'environment_unreadable'; return; }
      if (environment.targetConfigSeen || environment.otherGoogleSdkDeclared || state.appPreferred) {
        state.mirrorActive = false; state.appPreferred = true;
        fallbackReason = 'app_config_arrived_before_owned_config'; return;
      }
      // Do not initialize a new global queue/function. Main gtag must still be the
      // very same application-owned function and shared layer captured at declaration.
      if (typeof win.gtag !== 'function' || !Array.isArray(win.dataLayer) ||
          win.gtag !== state.originalGtag || win.dataLayer !== state.originalLayer) {
        fallbackReason = 'existing_main_transport_changed'; return;
      }
      if (!syncOwnedAnalyticsConsent() || !readConsent() || !googleConsentEvidence().analyticsGranted || state.mirrorFailed) {
        fallbackReason = 'owned_analytics_consent_not_ready'; return;
      }
      // Mark before the call: if a wrapper throws after accepting a command, no retry
      // may create a second config. This only suppresses initial page_view; enhanced
      // measurement and a later App config still require actual Network validation.
      state.configSubmitted = true;
      try {
        win.gtag.apply(win, ['config', measurementId, { send_page_view: false }]);
        fallbackConfigSubmitted = true;
        fallbackReason = 'owned_config_submitted_no_initial_page_view';
      } catch (_) {
        state.status = 'config_failed';
        fallbackReason = 'owned_config_failed_no_retry'; counters.errors += 1;
      }
    }

    function evaluateFallback() {
      fallbackTimer = null;
      if (!fallbackAllowed()) return;
      fallbackChecked = true;
      var shared = ownedFallbackState();
      if (shared === false) { fallbackReason = 'fallback_state_conflict'; publishDiagnostics(); return; }
      if (shared) {
        fallbackState = shared;
        finishOwnedFallback();
        if (refreshReadiness()) flushQueue();
        publishDiagnostics(); return;
      }
      var environment = mainEnvironment();
      if (environment.inspectionFailed || environment.targetConfigSeen || environment.googleSdkDeclared ||
          win.__bhKnownGoogleTagLoaded === true) {
        fallbackReason = environment.inspectionFailed ? 'environment_unreadable' :
          environment.targetConfigSeen ? 'app_target_config_exists' : 'google_sdk_already_declared';
        requestExistingGoogleTagRelease(); publishDiagnostics(); return;
      }
      if (typeof win.gtag !== 'function' || !Array.isArray(win.dataLayer)) {
        fallbackReason = 'existing_main_gtag_and_layer_required'; publishDiagnostics(); return;
      }
      var script;
      try {
        script = doc.createElement('script');
        script.setAttribute('async', '');
        script.setAttribute('data-bh-owned-google-sdk', 'v15');
        script.setAttribute('src', 'https://www.googletagmanager.com/gtag/js?id=' + BOOTSTRAP_TAG_ID);
        // The existing theme interceptor may defer src. Keep its original owner;
        // the consent-aware existing helper will hydrate this authenticated target.
        var state = {
          owner: 'bh-ga4-v15', measurementId: measurementId, bootstrapTagId: BOOTSTRAP_TAG_ID, status: 'declared',
          configSubmitted: false, originalGtag: win.gtag, originalLayer: win.dataLayer,
          script: script, mirrorActive: false, mirrorLast: null, mirrorCommands: 0,
          mirrorFailed: false, appPreferred: false
        };
        Object.defineProperty(win, '__bhGa4OwnedFallback', { value: state, configurable: true });
        fallbackState = state;
        var onLoad = function (event) {
          if (!event || event.isTrusted === false) return;
          try {
            var source = new win.URL(script.getAttribute('src'), win.location.href);
            if (source.origin !== 'https://www.googletagmanager.com' || source.pathname !== '/gtag/js' ||
                source.username || source.password || source.searchParams.getAll('id').length !== 1 ||
                source.searchParams.get('id') !== BOOTSTRAP_TAG_ID) return;
          } catch (_) { return; }
          state.status = 'loaded';
          win.__bhKnownGoogleTagLoaded = true;
          finishOwnedFallback();
          if (typeof win.Event === 'function' && typeof win.dispatchEvent === 'function') {
            win.dispatchEvent(new win.Event('bh:known-google-tag-loaded'));
          }
          if (refreshReadiness()) flushQueue();
          publishDiagnostics();
        };
        var onError = function () {
          if (state.status === 'loaded' || state.status === 'config_failed') return;
          state.status = 'load_failed'; fallbackReason = 'owned_sdk_load_failed_no_retry';
          counters.errors += 1; publishDiagnostics();
        };
        if (typeof script.addEventListener !== 'function' || !doc.head || typeof doc.head.appendChild !== 'function') {
          state.status = 'load_failed'; fallbackReason = 'script_load_listener_unavailable'; publishDiagnostics(); return;
        }
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', onError, { once: true });
        // One final synchronous environment/permission check narrows the race with
        // late App insertion. Future App behavior is outside this adapter's control.
        environment = mainEnvironment();
        if (!fallbackAllowed() || environment.inspectionFailed || environment.googleSdkDeclared || environment.targetConfigSeen) {
          state.status = 'cancelled'; fallbackReason = 'app_or_consent_arrived_before_append'; publishDiagnostics(); return;
        }
        state.status = 'loading';
        state.mirrorActive = true;
        // A page-owned handler survives adapter replacement: the downloaded SDK
        // remains present even when the click collector is destroyed. It never
        // grants permission itself and touches only analytics_storage.
        state.consentListener = function () { syncOwnedAnalyticsConsent(); };
        doc.addEventListener('visitorConsentCollected', state.consentListener);
        state.restoreConsentListener = function (event) {
          if (event && event.persisted === true) syncOwnedAnalyticsConsent();
        };
        win.addEventListener('pageshow', state.restoreConsentListener);
        doc.head.appendChild(script);
        fallbackReason = 'owned_sdk_loading';
        requestExistingGoogleTagRelease();
      } catch (_) {
        if (fallbackState) fallbackState.status = 'load_failed';
        fallbackReason = 'owned_sdk_declaration_failed_no_retry'; counters.errors += 1;
      }
      publishDiagnostics();
    }

    function considerFallback() {
      if (!fallbackAllowed()) return;
      var shared = ownedFallbackState();
      if (shared) {
        fallbackState = shared; finishOwnedFallback(); return;
      }
      if (shared === false || fallbackChecked || fallbackTimer !== null) return;
      var environment = mainEnvironment();
      if (environment.targetConfigSeen || environment.googleSdkDeclared || environment.inspectionFailed ||
          win.__bhKnownGoogleTagLoaded === true) {
        fallbackChecked = true;
        fallbackReason = 'existing_google_environment_preferred'; return;
      }
      fallbackReason = 'bounded_initial_detection';
      fallbackTimer = win.setTimeout(evaluateFallback, fallbackDelay);
    }

    function knownScript(node) {
      if (!node || String(node.tagName || node.nodeName).toUpperCase() !== 'SCRIPT') return false;
      try {
        var source = node.getAttribute && (node.getAttribute('src') || node.getAttribute('data-src'));
        if (!source) return false;
        var url = new win.URL(source, win.location.href);
        var ids = url.searchParams.getAll('id');
        if (url.origin !== 'https://www.googletagmanager.com' || url.username || url.password || ids.length !== 1) return false;
        return (url.pathname === '/gtag/js' && (ids[0] === 'GT-PHCDRJ3R' || ids[0] === measurementId)) ||
          (url.pathname === '/gtag/destination' && ids[0] === measurementId);
      } catch (_) { return false; }
    }

    function containsKnownScript(node) {
      if (knownScript(node)) return true;
      if (!node || typeof node.querySelectorAll !== 'function') return false;
      try {
        var scripts = node.querySelectorAll('script[src], script[data-src]');
        for (var index = 0; index < Math.min(scripts.length, 200); index += 1) {
          if (knownScript(scripts[index])) return true;
        }
      } catch (_) { /* Ignore unrelated malformed nodes. */ }
      return false;
    }

    function scheduleObserverBatch() {
      if (destroyed || observerBatchTimer !== null || !readConsent()) return;
      observerBatchTimer = win.setTimeout(function () {
        observerBatchTimer = null;
        if (destroyed || !readConsent()) { pauseForConsent(); publishDiagnostics(); return; }
        requestExistingGoogleTagRelease();
        if (refreshReadiness()) {
          flushQueue();
          if (!preflightProbeDone && options.preflightProbe !== false) {
            preflightProbeDone = true; probeIdentity();
          }
        } else if (queue.length || pendingHandoff) prepareTransport();
        publishDiagnostics();
      }, 0);
    }

    function startScriptObserver() {
      if (scriptObserver || destroyed || options.enabled !== true || !readConsent() ||
          !doc.head || typeof win.MutationObserver !== 'function') return;
      try {
        scriptObserver = new win.MutationObserver(function (records) {
          if (destroyed || !readConsent()) { pauseForConsent(); return; }
          for (var index = 0; index < Math.min(records.length, 200); index += 1) {
            var record = records[index];
            if (record.type === 'attributes' && knownScript(record.target)) { scheduleObserverBatch(); return; }
            if (record.type === 'childList') {
              var additions = record.addedNodes || [];
              for (var child = 0; child < Math.min(additions.length, 200); child += 1) {
                if (containsKnownScript(additions[child])) { scheduleObserverBatch(); return; }
              }
            }
          }
        });
        scriptObserver.observe(doc.head, { childList: true, subtree: true, attributes: true,
          attributeFilter: ['src', 'data-src', 'type'] });
      } catch (_) { scriptObserver = null; counters.errors += 1; lastReason = 'script_observer_unavailable'; }
    }

    function stopScriptObserver() {
      clearTimer('observer');
      if (!scriptObserver) return;
      try { scriptObserver.disconnect(); } catch (_) { /* No storefront side effects. */ }
      scriptObserver = null;
    }

    function onKnownSdkLoaded() {
      if (handoffTimingEnabled() && win.__bhKnownGoogleTagLoaded === true) noteTimingReadiness('sdkLoadedNotifiedAtMs');
      finishOwnedFallback();
      if (destroyed || !readConsent()) { pauseForConsent(); publishDiagnostics(); return; }
      // The capture/helper listener owns the authenticated *actual load* marker.
      // A plain SDK DOM declaration, event name or successful getter is insufficient.
      if (refreshReadiness()) {
        flushQueue();
        if (!preflightProbeDone && options.preflightProbe !== false) {
          preflightProbeDone = true; probeIdentity();
        }
      } else if (queue.length || pendingHandoff) prepareTransport();
      publishDiagnostics();
    }

    function expireQueue() {
      var current = now();
      var retained = [];
      queue.forEach(function (item) {
        if (current - item.createdAt >= item.ttlMs) counters.expired += 1;
        else retained.push(item);
      });
      queue = retained;
      clearTimer('queue');
      if (queue.length) {
        var remaining = Math.min.apply(null, queue.map(function (item) { return item.ttlMs - (current - item.createdAt); }));
        queueTimer = win.setTimeout(expireQueue, Math.max(1, remaining));
      } else if (!pendingHandoff) clearTimer('retry');
      publishDiagnostics();
    }

    function sameSitePath(value) {
      if (typeof value !== 'string' || !value) return null;
      try {
        var url = new win.URL(value, win.location.href);
        var current = new win.URL(win.location.href);
        var expectedHost = current.hostname.replace(/^www\./i, '');
        if ((url.protocol !== 'https:' && url.protocol !== 'http:') ||
            url.hostname.replace(/^www\./i, '') !== expectedHost ||
            url.username || url.password) return null;
        var path = url.pathname;
        if (path.length > 100 || hasObviousPii(path) || isPrivatePath(path) || /@|%40|%0[ad]/i.test(path)) return null;
        return path;
      } catch (_) { return null; }
    }

    function sanitize(payload) {
      if (!payload || typeof payload !== 'object') return null;
      var area = payload.bh_area;
      if (!Object.prototype.hasOwnProperty.call(AREA_ENTRIES, area) ||
          AREA_ENTRIES[area].indexOf(payload.entry_type) < 0 ||
          PAGE_TYPES.indexOf(payload.page_type) < 0 ||
          !validKey(payload.content_id) || !validKey(payload.block_key)) return null;
      var labels = options.contentLabels;
      if (!labels || typeof labels !== 'object' || Array.isArray(labels)) return null;
      var labelDescriptor = Object.getOwnPropertyDescriptor(labels, payload.content_id);
      if (!labelDescriptor || !Object.prototype.hasOwnProperty.call(labelDescriptor, 'value')) return null;
      var label = labelDescriptor.value;
      if (typeof label !== 'string' || label.length < 1 || label.length > 100 || label.trim() !== label ||
          /[\u0000-\u001f\u007f<>]/.test(label) || hasObviousPii(label) ||
          /(?:email|e-mail|phone|telephone|mobile|customer_id|token|password)\s*=/i.test(label) ||
          payload.content_label !== label) return null;
      var result = {
        bh_area: area, entry_type: payload.entry_type, content_id: payload.content_id,
        content_label: label,
        block_key: payload.block_key, page_type: payload.page_type
      };
      var complete = true;
      SCENE_KEYS[area].forEach(function (key) {
        var value = payload[key];
        if (key === 'banner_index' || key === 'position') {
          if (!Number.isInteger(value) || value < 1 || value > 10000) complete = false;
          else result[key] = value;
        } else if (key === 'menu_type') {
          if (value !== 'product_category' && value !== 'collection_series') complete = false;
          else result[key] = value;
        } else {
          if (!validKey(value)) complete = false;
          else result[key] = value;
        }
      });
      if (!complete) return null;
      if (payload.target_url) {
        var target = sameSitePath(payload.target_url);
        if (target) result.target_url = target;
        else counters.omittedUrls += 1;
      }
      return result;
    }

    function hasObviousPii(value) {
      var text = String(value || '');
      try { text = decodeURIComponent(text); } catch (_) { /* retain original for checking */ }
      return /[\w.+\-]+@[\w.\-]+\.[a-z]{2,}/i.test(text) ||
        /(?:^|[?&\s])(?:email|e-mail|phone|telephone|mobile|customer_id|customerid|token|password)=/i.test(text) ||
        /(?:^|[^a-z\d])\+?\d[\d\s().\-]{5,}\d(?:$|[^a-z\d])/i.test(text);
    }

    function piiInspectionUrl(value) {
      // A public Shopify preview ID identifies the authorized theme, not a visitor.
      // Remove only that exact same-site, same-Draft parameter from the inspection
      // copy. Keep the browser URL, event defaults and every other parameter intact.
      try {
        var url = new win.URL(value);
        var current = new win.URL(win.location.href);
        var theme = win.Shopify && win.Shopify.theme;
        var ids = url.searchParams.getAll('preview_theme_id');
        if (matchingDraft() && String(theme.id) === '196364992838' &&
            url.protocol === current.protocol && /^https?:$/.test(url.protocol) &&
            url.hostname.replace(/^www\./i, '') === current.hostname.replace(/^www\./i, '') &&
            url.port === current.port && !url.username && !url.password && ids.length === 1 &&
            ids[0] === String(theme.id)) {
          url.searchParams.delete('preview_theme_id');
          return url.href;
        }
      } catch (_) { /* An invalid/non-public value receives no exception. */ }
      return value;
    }

    function isPrivatePath(value) {
      var path = String(value || '');
      try { path = decodeURIComponent(path); } catch (_) { /* retain original */ }
      return /(?:^|\/)(?:accounts?|checkouts?|customers?|orders?|password|reset|reset-password|tokens?)(?:\/|$)/i.test(path);
    }

    function hasPrivateReferrer() {
      var referrer = String(doc.referrer || '');
      if (hasObviousPii(piiInspectionUrl(referrer))) return true;
      try { return !!referrer && isPrivatePath(new win.URL(referrer).pathname); }
      catch (_) { return false; }
    }

    function eventContext(params) {
      // Event-level PII protection only. Never rewrite the real URL or global tag settings.
      var href = String(win.location.href || '');
      var privatePage = false;
      try { privatePage = isPrivatePath(new win.URL(href).pathname); } catch (_) { /* handled below */ }
      if (hasObviousPii(piiInspectionUrl(href)) || privatePage) {
        try {
          var page = new win.URL(href);
          params.page_location = page.origin + (hasObviousPii(page.pathname) || isPrivatePath(page.pathname) ? '/' : page.pathname);
        } catch (_) { params.page_location = 'https://www.baroquehour.com/'; }
      }
      if (hasObviousPii(doc.title)) params.page_title = 'Baroque Hour storefront';
      // Override only this event's inherited field. Never rewrite document.referrer,
      // global Google configuration, existing App signals or benign campaign URLs.
      if (hasPrivateReferrer()) params.page_referrer = '';
    }

    function initNavigationHandoff() {
      var settings = options.navigationHandoff || {};
      if (options.enabled !== true || settings.enabled !== true || !deploymentAllowed() ||
          (!explicitDeployment && (!matchingDraft() || String(settings.draftThemeId) !== '196364992838')) || !win.BhClickHandoff ||
          typeof win.BhClickHandoff.create !== 'function') return;
      try {
        handoff = win.BhClickHandoff.create({ window: win, now: now, enabled: true,
          draftThemeId: '196364992838', ttlMs: 45000,
          ...(explicitDeployment ? { deployment: deployment } : {}) });
      } catch (_) { handoff = null; counters.errors += 1; }
    }

    function clearPendingHandoff(reason) {
      if (pendingHandoff && reason) noteTimingClear(reason);
      pendingHandoff = null;
      if (handoffTimer !== null) { win.clearTimeout(handoffTimer); handoffTimer = null; }
      if (!queue.length) clearTimer('retry');
    }

    function navigationContext(params, navigation) {
      if (!handoff || !navigation || navigation.sameTab !== true ||
          !navigation.event || navigation.event.isTrusted !== true || navigation.event.defaultPrevented === true ||
          !navigation.element || typeof navigation.element.getAttribute !== 'function' || !params.target_url) return null;
      try {
        var current = new win.URL(win.location.href);
        var target = new win.URL(navigation.element.getAttribute('href'), current.href);
        var linkTarget = navigation.element.getAttribute('target');
        if (target.origin !== current.origin || target.username || target.password ||
            (linkTarget && linkTarget.toLowerCase() !== '_self') || navigation.element.hasAttribute('download')) return null;
        var sourcePath = sameSitePath(current.href), targetPath = sameSitePath(target.href);
        var clickedAt = Number(navigation.createdAt);
        if (!sourcePath || !targetPath || sourcePath === targetPath || targetPath !== params.target_url ||
            !Number.isFinite(clickedAt) || clickedAt < 0 || clickedAt > now() || now() - clickedAt > 1500) return null;
        return { event: navigation.event, element: navigation.element, href: target.href,
          sourcePath: sourcePath, targetPath: targetPath, createdAt: clickedAt };
      } catch (_) { return null; }
    }

    function navigationStillValid(navigation) {
      try {
        if (!navigation || navigation.event.defaultPrevented === true || navigation.element.isConnected === false) return false;
        var target = new win.URL(navigation.element.getAttribute('href'), win.location.href);
        var linkTarget = navigation.element.getAttribute('target');
        return target.href === navigation.href && !navigation.element.hasAttribute('download') &&
          (!linkTarget || linkTarget.toLowerCase() === '_self');
      } catch (_) { return false; }
    }

    function handoffSourceAllowed(record, params) {
      var scope = options.scopes && options.scopes[params.bh_area];
      return !!options.areas && options.areas[params.bh_area] === true && !!scope &&
        Array.isArray(scope.pageTypes) && scope.pageTypes.indexOf(params.page_type) >= 0 &&
        (!scope.paths || (Array.isArray(scope.paths) && scope.paths.indexOf(record.sourcePath) >= 0));
    }

    function claimNavigationHandoff() {
      if (!handoff || handoffChecked || !readConsent() || destroyed || pageHidden) return;
      handoffChecked = true;
      var record = handoff.take();
      if (!record) return;
      noteTimingRecord(record.createdAt, 'claimed');
      var label = options.contentLabels && options.contentLabels[record.params.content_id];
      var params = sanitize(Object.assign({}, record.params, { content_label: label }));
      if (!params || !handoffSourceAllowed(record, params)) { counters.handoffDropped += 1; noteTimingClear('invalid_source_scope'); return; }
      var remaining = 45000 - (now() - record.createdAt);
      if (remaining <= 0) { counters.handoffExpired += 1; noteTimingClear('expired'); return; }
      pendingHandoff = { params: params, sourcePath: record.sourcePath, createdAt: record.createdAt };
      counters.handoffClaimed += 1;
      handoffTimer = win.setTimeout(function () {
        handoffTimer = null;
        if (pendingHandoff) { clearPendingHandoff('expired'); counters.handoffExpired += 1; publishDiagnostics(); }
      }, remaining);
    }

    function flushNavigationHandoff() {
      if (!pendingHandoff || !transportReady || destroyed || pageHidden || !readConsent()) return;
      var record = pendingHandoff;
      clearPendingHandoff('submission_attempt');
      var age = now() - record.createdAt;
      if (age < 0 || age >= 45000) { counters.handoffExpired += 1; noteTimingClear('expired'); return; }
      var page = new win.URL(win.location.href);
      if (send(record.params, { pageLocation: page.origin + record.sourcePath, delayMs: Math.floor(age) }))
        counters.handoffSubmitted += 1;
      else { counters.handoffDropped += 1; noteTimingClear('send_failed'); }
      // The record was removed from storage at claim and detached from memory
      // before this single attempt. Neither callback absence nor throw retries it.
    }

    function send(params, sourceContext) {
      if (!started || destroyed || pageHidden) {
        lastReason = 'inactive_page_no_event_submission'; publishDiagnostics(); return false;
      }
      // Permission is checked again at send time, including when a short queue is flushed.
      allowed = readConsent();
      if (!allowed || !ownedGoogleAnalyticsAllowed()) {
        counters.denied += 1; pauseForConsent(); lastReason = 'analytics_not_allowed_at_send'; publishDiagnostics(); return false;
      }
      var transport = commandFunction();
      if (!transportReady || !transport.call) return false;
      if (readyOwnerSeen && (win.gtag !== readyOwnerGtag || win.dataLayer !== readyOwnerLayer)) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'existing_transport_changed_before_send';
        return false;
      }
      var eventParams = Object.assign({}, params, { send_to: measurementId });
      if (options.debugMode === true && matchingDraft()) eventParams.debug_mode = true;
      eventContext(eventParams);
      if (sourceContext) {
        // Only the internally validated, once-claimed handoff can override
        // context. Never inherit the destination page's title or referrer.
        eventParams.page_location = sourceContext.pageLocation;
        eventParams.page_title = 'Baroque Hour storefront';
        eventParams.page_referrer = '';
        eventParams.bh_delivery = 'next_page';
        eventParams.bh_delivery_delay_ms = sourceContext.delayMs;
      }
      var callbackObserved = false;
      // Google-supported command-processing callback/timeout. This does not
      // prove receipt and never delays navigation, retries or changes consent.
      eventParams.event_timeout = 2000;
      eventParams.event_callback = function () {
        if (callbackObserved) return;
        callbackObserved = true;
        if (!draftDiagnosticsEnabled()) return;
        counters.processingCallbacks += 1;
        publishDiagnostics();
      };
      try {
        // A synchronous permission/context getter may have hidden or destroyed
        // this document since the first guard. Never submit after that boundary.
        if (!started || destroyed || pageHidden) {
          lastReason = 'inactive_page_no_event_submission'; publishDiagnostics(); return false;
        }
        transport.call('event', 'bh_click', eventParams);
        counters.sentCommands += 1;
        if (draftDiagnosticsEnabled()) {
          var diagnosticParams = Object.assign({}, eventParams);
          delete diagnosticParams.event_callback;
          recentCommands.push({ event: 'bh_click', params: diagnosticParams });
          if (recentCommands.length > 5) recentCommands.shift();
        }
        lastReason = 'event_command_submitted';
        probeIdentity(); publishDiagnostics();
        return true;
      } catch (_) {
        // Never retry a possibly-submitted event through a second path.
        counters.errors += 1; lastReason = 'event_command_failed'; publishDiagnostics(); return false;
      }
    }

    function flushQueue() {
      if (!started || destroyed || pageHidden || !transportReady) return;
      flushNavigationHandoff();
      expireQueue();
      var pending = queue;
      queue = []; clearTimer('queue');
      // A submitted command may synchronously invoke a page lifecycle handler.
      // The detached remainder is discarded, never replayed on BFCache restore.
      for (var index = 0; index < pending.length; index += 1) {
        if (!started || destroyed || pageHidden || !transportReady) break;
        send(pending[index].params);
      }
    }

    function refreshReadiness() {
      if (!started || destroyed || pageHidden || !readConsent()) { transportReady = false; readinessBasis = 'none'; return false; }
      if (!ownedGoogleAnalyticsAllowed()) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'owned_google_analytics_not_allowed'; return false;
      }
      if (win.__bhKnownGoogleTagLoaded !== true) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'authenticated_sdk_load_not_seen'; return false;
      }
      var environment = mainEnvironment();
      if (environment.nonDefaultKnownSdkLayerDeclared) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'known_sdk_layer_does_not_match_default_layer'; return false;
      }
      if (readyOwnerSeen && (win.gtag !== readyOwnerGtag || win.dataLayer !== readyOwnerLayer)) {
        // A copied historical config is not evidence that a new array/function
        // is the SDK's subscribed owner. Revalidate with bounded current getters.
        if (!ownerRevalidationRequired || pendingOwnerGtag !== win.gtag || pendingOwnerLayer !== win.dataLayer) {
          ownerRevalidationRequired = true; pendingOwnerGtag = win.gtag; pendingOwnerLayer = win.dataLayer;
          clearTimer('probe'); probeVersion += 1;
          unobservedConfigIdentityReadable = false; clientIdPresent = false; sessionIdPresent = false;
          identityProbeCount = 0; identityProbeState = 'transport_changed';
        }
      }
      var owned = ownedFallbackState();
      var ownedReady = !!owned && owned.status === 'loaded' && owned.configSubmitted &&
        fallbackConfigSubmitted && win.gtag === owned.originalGtag && win.dataLayer === owned.originalLayer;
      // An observed config is descriptive evidence, not proof that the current
      // function/layer is subscribed. All non-owned reuse requires both actual
      // G8 field callbacks on that same current transport; this does not initialize
      // the target or prove native identity, session continuity or receipt.
      if (environment.inspectionFailed && !environment.targetConfigSeen && !ownedReady) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'existing_main_environment_unreadable'; return false;
      }
      if (!commandFunction().call) {
        transportReady = false; readinessBasis = 'none'; lastReason = 'existing_main_transport_unavailable'; return false;
      }
      var revalidatingOwner = ownerRevalidationRequired;
      if (!ownedReady) {
        // Every non-owned reuse path needs current target field callbacks. A
        // historical visible config cannot authenticate the first owner either.
        // The unchanged owned path stays exact-Draft; only a genuine App takeover
        // of a cancelled/in-flight owned attempt may use this read-only path.
        if (owned === false || (owned && !(owned.appPreferred === true && environment.targetConfigSeen))) {
          transportReady = false; readinessBasis = 'none'; lastReason = 'owned_configuration_not_ready'; return false;
        }
        if (identityGtag !== win.gtag || identityLayer !== win.dataLayer) {
          unobservedConfigIdentityReadable = false;
          clientIdPresent = false; sessionIdPresent = false;
        }
        if (!unobservedConfigIdentityReadable) {
          transportReady = false; readinessBasis = 'none'; lastReason = 'existing_target_identity_not_readable';
          probeIdentity(true);
          if (!unobservedConfigIdentityReadable) return false;
        }
      }
      if (!transportReady) noteTimingReadiness('targetTransportReadyAtMs');
      transportReady = true;
      readinessBasis = ownedReady ? 'owned_sdk_load_and_config_command' :
        (revalidatingOwner ? 'existing_app_sdk_load_and_identity_readable_after_owner_change' :
          (environment.targetConfigSeen ? 'existing_app_sdk_load_config_and_target_identity_readable' : 'existing_app_sdk_load_and_target_identity_readable'));
      readyOwnerSeen = true; readyOwnerGtag = win.gtag; readyOwnerLayer = win.dataLayer;
      ownerRevalidationRequired = false; pendingOwnerGtag = null; pendingOwnerLayer = null;
      clearTimer('retry');
      lastReason = 'existing_app_transport_ready';
      return true;
    }

    function scheduleProbeRetry() {
      if (destroyed || pageHidden || !readConsent() || transportReady || (!queue.length && !pendingHandoff) || retryTimer !== null || probeCount >= maxProbes) return;
      // Handoff-only waiting starts its bounded target probe round on the
      // authenticated SDK load notification, not a blind pre-SDK timer loop.
      if (pendingHandoff && !queue.length && win.__bhKnownGoogleTagLoaded !== true) return;
      retryTimer = win.setTimeout(function () {
        retryTimer = null; probeTransport();
      }, 250);
    }

    function probeTransport(retryAllowed) {
      if (destroyed || !readConsent()) return;
      if (!(pendingHandoff && !queue.length && win.__bhKnownGoogleTagLoaded !== true)) probeCount += 1;
      if (refreshReadiness()) {
        flushQueue();
        if (!preflightProbeDone && options.preflightProbe !== false) {
          preflightProbeDone = true; probeIdentity();
        }
      } else if (retryAllowed !== false) scheduleProbeRetry();
      publishDiagnostics();
    }

    function probeIdentity(forReadiness) {
      if (destroyed || pageHidden || !readConsent() || (!transportReady && forReadiness !== true) || transportProbeTimer !== null ||
          identityProbeCount >= 3 || (clientIdPresent && sessionIdPresent &&
          (forReadiness !== true || unobservedConfigIdentityReadable))) return;
      var transport = commandFunction();
      if (!transport.call) return;
      // Only object/function references and boolean presence flags are retained.
      // Never save, expose, forward, replace or manufacture an identity value.
      var originalGtag = win.gtag, originalLayer = win.dataLayer;
      identityGtag = originalGtag; identityLayer = originalLayer;
      identityProbeCount += 1; identityProbeState = 'pending';
      var version = ++probeVersion;
      var gotClient = false, gotSession = false;
      var presentClient = false, presentSession = false;
      var commandsReturned = false;
      function finishProbeIfComplete() {
        if (!commandsReturned || !gotClient || !gotSession || destroyed || pageHidden ||
            version !== probeVersion || !readConsent()) return;
        clearTimer('probe');
        identityProbeState = clientIdPresent && sessionIdPresent ? 'values_present' : 'values_unavailable';
        if (forReadiness === true && clientIdPresent && sessionIdPresent) {
          unobservedConfigIdentityReadable = true;
          // Re-read permission, authenticated load and the public environment
          // before flushing only still-current, consent-permitted short queues.
          if (refreshReadiness()) flushQueue();
        }
      }
      function onValue(kind, value) {
        if (destroyed || pageHidden || version !== probeVersion || !readConsent()) return;
        if (win.gtag !== originalGtag || win.dataLayer !== originalLayer) {
          clearTimer('probe'); probeVersion += 1; unobservedConfigIdentityReadable = false;
          clientIdPresent = false; sessionIdPresent = false;
          identityProbeState = 'transport_changed'; publishDiagnostics(); return;
        }
        if ((kind === 'client' && gotClient) || (kind !== 'client' && gotSession)) return;
        if (kind === 'client') {
          gotClient = true; presentClient = typeof value === 'string' && value.trim().length > 0;
        } else {
          gotSession = true; presentSession = (typeof value === 'string' && value.trim().length > 0) ||
            (typeof value === 'number' && Number.isFinite(value) && value > 0);
        }
        clientIdPresent = presentClient; sessionIdPresent = presentSession;
        finishProbeIfComplete();
        publishDiagnostics();
      }
      transportProbeTimer = win.setTimeout(function () {
        transportProbeTimer = null; probeVersion += 1; identityProbeState = 'timeout'; publishDiagnostics();
      }, probeTimeout);
      try {
        transport.call('get', measurementId, 'client_id', function (value) { onValue('client', value); });
        transport.call('get', measurementId, 'session_id', function (value) { onValue('session', value); });
        commandsReturned = true; finishProbeIfComplete();
      } catch (_) {
        clearTimer('probe'); probeVersion += 1; unobservedConfigIdentityReadable = false;
        counters.errors += 1; identityProbeState = 'failed'; publishDiagnostics();
      }
    }

    function mainEnvironment() {
      var result = { googleSdkDeclared: false, otherGoogleSdkDeclared: false, targetConfigSeen: false, nonDefaultKnownSdkLayerDeclared: false, inspectionFailed: false };
      try {
        Array.prototype.forEach.call(doc.querySelectorAll('script[src], script[data-src]'), function (node) {
          var source = (node.getAttribute && (node.getAttribute('src') || node.getAttribute('data-src'))) || node.src || '';
          if (!source) return;
          try {
            var url = new win.URL(source, win.location.href);
            if (url.hostname === 'www.googletagmanager.com' &&
                ['/gtag/js', '/gtag/destination', '/gtm.js'].indexOf(url.pathname) >= 0) {
              result.googleSdkDeclared = true;
              var sourceIds = url.searchParams.getAll('id');
              if (url.pathname === '/gtag/js' && sourceIds.length === 1 &&
                  (sourceIds[0] === measurementId || sourceIds[0] === BOOTSTRAP_TAG_ID)) {
                var sourceLayers = url.searchParams.getAll('l');
                if (sourceLayers.length > 1 || (sourceLayers.length === 1 && sourceLayers[0] !== 'dataLayer')) {
                  result.nonDefaultKnownSdkLayerDeclared = true;
                }
              }
              var owned = ownedFallbackState();
              if ((!owned || node !== owned.script) && ['/gtag/js', '/gtm.js'].indexOf(url.pathname) >= 0) result.otherGoogleSdkDeclared = true;
            }
          } catch (_) { /* unrelated script */ }
        });
        if (Array.isArray(win.dataLayer)) {
          // Read only data properties. Do not execute getters or inspect arbitrary app internals.
          var limit = Math.min(win.dataLayer.length, 2000);
          if (win.dataLayer.length > limit) result.inspectionFailed = true;
          for (var index = 0; index < limit; index += 1) {
            var entryDescriptor = Object.getOwnPropertyDescriptor(win.dataLayer, String(index));
            if (!entryDescriptor) continue;
            if (!Object.prototype.hasOwnProperty.call(entryDescriptor, 'value')) {
              result.inspectionFailed = true; continue;
            }
            var entry = entryDescriptor.value;
            if (!entry || (typeof entry !== 'object' && typeof entry !== 'function')) continue;
            var command = Object.getOwnPropertyDescriptor(entry, '0');
            var target = Object.getOwnPropertyDescriptor(entry, '1');
            if (command && command.value === 'config' && target &&
                (target.value === measurementId || target.value === 'GT-PHCDRJ3R')) result.targetConfigSeen = true;
          }
        } else if (win.dataLayer != null) result.inspectionFailed = true;
      } catch (_) { result.inspectionFailed = true; }
      return result;
    }

    function prepareTransport() {
      if (destroyed || !readConsent()) return;
      probeTransport();
    }

    function init() {
      if (started || destroyed) return api;
      if (!deploymentAllowed()) { lastReason = 'deployment_scope_not_allowed'; return api; }
      started = true;
      initNavigationHandoff();
      doc.addEventListener('visitorConsentCollected', updateConsent);
      win.addEventListener('pagehide', onPageHide);
      win.addEventListener('pageshow', onPageShow);
      win.addEventListener('bh:known-google-tag-loaded', onKnownSdkLoaded);
      if (win.Shopify && win.Shopify.customerPrivacy &&
          typeof win.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
        privacyReady = true; updateConsent();
      } else if (win.Shopify && typeof win.Shopify.loadFeatures === 'function') {
        try {
          win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
            if (destroyed) return;
            if (error || !win.Shopify.customerPrivacy) {
              lastReason = 'privacy_load_failed'; counters.errors += 1; publishDiagnostics(); return;
            }
            privacyReady = true; updateConsent();
          });
        } catch (_) { counters.errors += 1; lastReason = 'privacy_load_failed'; }
      } else lastReason = 'privacy_api_unavailable';
      publishDiagnostics();
      return api;
    }

    function emitBhClick(payload, navigation) {
      counters.received += 1;
      if (destroyed || pageHidden) {
        lastReason = 'inactive_page_no_event_submission'; publishDiagnostics(); return false;
      }
      if (!started || !readConsent() || !ownedGoogleAnalyticsAllowed()) {
        pauseForConsent();
        counters.denied += 1; lastReason = !started ? 'not_started' : 'analytics_not_allowed'; publishDiagnostics(); return false;
      }
      var params = sanitize(payload);
      if (!params) { counters.invalid += 1; lastReason = 'invalid_click_payload'; publishDiagnostics(); return false; }
      // A target may initialize behind unchanged App function/layer references
      // after an earlier bounded round exhausted. Only a new permitted valid
      // click can renew that budget; timers/SDK notifications never renew it.
      if (!transportReady && identityProbeCount >= 3 && transportProbeTimer === null) {
        probeVersion += 1; identityProbeCount = 0; identityProbeState = 'not_requested';
        clientIdPresent = false; sessionIdPresent = false;
        unobservedConfigIdentityReadable = false; identityGtag = null; identityLayer = null;
      }
      allowed = true; startScriptObserver(); requestExistingGoogleTagRelease(); considerFallback();
      if (refreshReadiness()) return send(params);
      // Consent/helper/getter callbacks can change page lifecycle synchronously.
      if (destroyed || pageHidden) {
        lastReason = 'inactive_page_no_event_submission'; publishDiagnostics(); return false;
      }
      // SDKs can be intentionally delayed until interaction. Each later permitted click
      // can re-arm one bounded attempt, without startup polling or an indefinite interval.
      if (retryTimer === null) probeCount = 0;
      if (!maxQueue || !queueTtl) { lastReason = 'transport_not_ready'; prepareTransport(); return false; }
      expireQueue();
      if (queue.length >= maxQueue) { counters.overflow += 1; lastReason = 'queue_full'; publishDiagnostics(); return false; }
      var handoffNavigation = navigationContext(params, navigation);
      var itemTtl = params.bh_area === 'load_more' && !handoffNavigation ? loadMoreQueueTtl : queueTtl;
      queue.push({ createdAt: handoffNavigation ? handoffNavigation.createdAt : now(), params: params, navigation: handoffNavigation, ttlMs: itemTtl });
      counters.queued += 1;
      expireQueue(); // Re-arm the earliest deadline when short and long waits coexist.
      lastReason = 'queued_with_analytics_permission';
      prepareTransport();
      publishDiagnostics();
      return true;
    }

    function onPageHide() {
      // Freeze source submission before detaching. A source command attempted
      // before this point has already left this queue and cannot be handed off.
      pageHidden = true; transportReady = false; readinessBasis = 'none'; stopFallbackTimer();
      var detached = queue;
      queue = [];
      clearPendingHandoff('pagehide');
      if (handoff) {
        handoff.clear();
        if (readConsent()) {
          for (var i = detached.length - 1; i >= 0; i -= 1) {
            var item = detached[i];
            if (navigationStillValid(item.navigation) && now() >= item.createdAt && now() - item.createdAt < queueTtl) {
              if (handoff.persist({ params: item.params, sourcePath: item.navigation.sourcePath,
                  targetPath: item.navigation.targetPath, createdAt: item.createdAt })) {
                counters.handoffStored += 1; noteTimingRecord(item.createdAt, 'stored');
              }
              break;
            }
          }
        }
      }
      clearQueue('pagehide'); clearTimer('probe'); clearTimer('retry'); probeVersion += 1; stopScriptObserver();
      unobservedConfigIdentityReadable = false; identityGtag = null; identityLayer = null;
      recentCommands = []; publishDiagnostics();
    }

    function onPageShow(event) {
      if (!event || event.persisted !== true || destroyed || !started) return;
      // BFCache keeps this document and all original script state. Re-read the
      // official API before resuming; never replay a pre-navigation click or
      // repeat an already-owned SDK/config/page_view to "warm up" the page.
      pageHidden = false; bfcacheRestores += 1; stopFallbackTimer();
      clearPendingHandoff('bfcache');
      if (handoff) handoff.onPageShow(event);
      noteTimingClear('bfcache');
      clearQueue('bfcache'); clearTimer('probe'); clearTimer('retry'); stopScriptObserver();
      probeVersion += 1; probeCount = 0; preflightProbeDone = false;
      transportReady = false; readinessBasis = 'none';
      clientIdPresent = false; sessionIdPresent = false;
      unobservedConfigIdentityReadable = false; identityGtag = null; identityLayer = null;
      identityProbeCount = 0; identityProbeState = 'not_requested';
      fallbackChecked = false; recentCommands = [];
      updateConsent();
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true; allowed = false; transportReady = false; stopFallbackTimer();
      doc.removeEventListener('visitorConsentCollected', updateConsent);
      win.removeEventListener('pagehide', onPageHide);
      win.removeEventListener('pageshow', onPageShow);
      win.removeEventListener('bh:known-google-tag-loaded', onKnownSdkLoaded);
      stopScriptObserver();
      clearPendingHandoff('destroy');
      if (handoff) handoff.clear();
      noteTimingClear('destroy');
      clearQueue('destroy'); clearTimer('probe'); clearTimer('retry'); probeVersion += 1;
      // Existing Google library and config belong to the platform, so never remove them.
      lastReason = 'destroyed';
      recentCommands = []; publishDiagnostics();
    }

    function diagnostics() {
      return {
        version: ADAPTER_VERSION, route: route, measurementId: measurementId, bootstrapTagId: BOOTSTRAP_TAG_ID, started: started, destroyed: destroyed,
        gtagType: typeof win.gtag,
        dataLayerPresent: Array.isArray(win.dataLayer), transportFunction: commandFunction().type,
        readyOwnerEstablished: readyOwnerSeen,
        currentTransportMatchesReadyOwner: readyOwnerSeen ? win.gtag === readyOwnerGtag && win.dataLayer === readyOwnerLayer : null,
        transportOwnerRevalidationPending: ownerRevalidationRequired,
        privacyReady: privacyReady, analyticsAllowed: readConsent(), transportReady: transportReady,
        clientIdPresent: clientIdPresent, sessionIdPresent: sessionIdPresent,
        queuedNow: queue.length, probeCount: probeCount, lastReason: lastReason,
        navigationHandoffEnabled: !!handoff, navigationHandoffPending: !!pendingHandoff,
        handoffTimingEvidence: timingSnapshot(),
        existingGoogleTagReleaseRequested: existingGoogleTagReleaseRequested,
        releaseAttempts: releaseAttempts, scriptObserverActive: scriptObserver !== null,
        authenticatedSdkLoaded: win.__bhKnownGoogleTagLoaded === true, readinessBasis: readinessBasis,
        identityProbeCount: identityProbeCount, identityProbeState: identityProbeState,
        targetIdentityReadable: unobservedConfigIdentityReadable,
        targetIdentityReadableWithoutConfig: unobservedConfigIdentityReadable && !mainEnvironment().targetConfigSeen,
        guardedFallback: options.guardedFallback === true,
        pageHidden: pageHidden, bfcacheRestores: bfcacheRestores,
        enabledAreaForPage: hasEnabledAreaForPage(),
        draftOnlyOwnedAnalyticsConsentMirror: options.draftOnlyOwnedAnalyticsConsentMirror === true,
        ownedAnalyticsConsentMirrorActive: !!fallbackState && fallbackState.mirrorActive === true,
        ownedAnalyticsConsentLast: fallbackState ? fallbackState.mirrorLast : null,
        ownedAnalyticsConsentCommands: fallbackState ? fallbackState.mirrorCommands : 0,
        ownedAnalyticsConsentFailed: !!fallbackState && fallbackState.mirrorFailed === true,
        googleConsentEvidence: googleConsentEvidence(),
        fallbackDetectionMs: fallbackDelay, fallbackReason: fallbackReason,
        fallbackStatus: fallbackState ? fallbackState.status : 'none',
        fallbackConfigSubmitted: fallbackConfigSubmitted,
        mainEnvironment: mainEnvironment(),
        counters: Object.assign({}, counters),
        recentEventCommands: draftDiagnosticsEnabled() ? recentCommands.slice() : [],
        deliveryProof: 'Commands and readiness are not GA4 receipt or shared-session proof.'
      };
    }

    var api = { init: init, emitBhClick: emitBhClick, destroy: destroy, diagnostics: diagnostics };
    return api;
  }

  function bootstrap(win) {
    if (win.BhGA4Sender && typeof win.BhGA4Sender.diagnostics === 'function') return win.BhGA4Sender;
    var node = win.document.getElementById('bh-tracking-config');
    if (!node) return null;
    var config;
    try { config = JSON.parse(node.textContent || '{}'); } catch (_) { return null; }
    if (config.enabled === false || (config.ga4 && config.ga4.enabled === false)) return null;
    var options = Object.assign({}, config.ga4 || {}, { window: win, enabled: config.enabled === true,
      pageType: config.pageType, areas: config.areas, scopes: config.scopes, contentLabels: config.contentLabels, diagnostics: config.diagnostics || {} });
    var sender;
    try { sender = createAdapter(options).init(); } catch (_) { return null; }
    win.BhGA4Sender = sender;
    win.emitBhClick = sender.emitBhClick;
    return sender;
  }

  return { create: createAdapter, bootstrap: bootstrap, measurementId: MEASUREMENT_ID, bootstrapTagId: BOOTSTRAP_TAG_ID, version: ADAPTER_VERSION };
});
